import axios from 'axios';
import api from './api';
import { sendToProjectChat } from './chats';
import { TunableValues } from './editorHost';

export interface VersionTunables {
  applied: TunableValues;
  pending: TunableValues;
}

export async function getVersionTunables(projectId: string, versionId: string): Promise<VersionTunables> {
  const { data } = await api.get<VersionTunables>(`project/${projectId}/versions/${versionId}/tunables`);
  return data;
}

export async function savePendingTunables(
  projectId: string,
  versionId: string,
  pending: TunableValues,
): Promise<void> {
  await api.put(`project/${projectId}/versions/${versionId}/tunables/pending`, pending);
}

/** What became of a request that starts a job: it was queued as `jobId`, the project is busy, or it failed. */
export type JobRequestOutcome =
  | { status: 'started'; jobId: string }
  | { status: 'busy' }
  | { status: 'failed'; error: unknown };

async function requestJob(start: () => Promise<string>): Promise<JobRequestOutcome> {
  try {
    return { status: 'started', jobId: await start() };
  } catch (error) {
    return axios.isAxiosError(error) && error.response?.status === 409 ? { status: 'busy' } : { status: 'failed', error };
  }
}

async function postJob(path: string, body?: unknown): Promise<string> {
  const { data } = await api.post<{ jobId: string }>(path, body);
  return data.jobId;
}

/** Asks the chat to make the value the creator described editable, as a job that makes a new version. */
export function exposeTunable(projectId: string, text: string): Promise<JobRequestOutcome> {
  return requestJob(() => postJob(`project/${projectId}/tunables/expose`, { text }));
}

/** Sends the creator's own words to the project's chat, as a request typed there. */
export function askChat(projectId: string, text: string): Promise<JobRequestOutcome> {
  return requestJob(() => sendToProjectChat(projectId, text));
}

/** The tool tree of this version, or null while its values have never been arranged. */
export async function getVersionLayout(projectId: string, versionId: string): Promise<unknown | null> {
  try {
    const { data } = await api.get(`project/${projectId}/versions/${versionId}/layout`);
    return data || null;
  } catch {
    return null;
  }
}

/** Queues the job that arranges the version's values into panels. */
export function organizeTunables(projectId: string, versionId: string): Promise<JobRequestOutcome> {
  return requestJob(() => postJob(`project/${projectId}/versions/${versionId}/organize`));
}
